import {
  TABLE_TOOLBAR_BUTTON_CLASSNAME,
  TABLE_TOOLBAR_LEADING_ICON_CLASSNAME,
  TABLE_TOOLBAR_SEARCH_ICON_CLASSNAME,
  TABLE_TOOLBAR_SEARCH_INPUT_CLASSNAME,
} from "@src/lib/utils/tableToolbarChrome";
import { cn } from "@src/lib/utils/cn";
import MultiSelectBanner from "@src/lib/ui/MultiSelectBanner";
import { TableContentRow } from "@src/lib/ui/Table";
import {
  computeTableColumnSizing,
  getHorizontalScrollTableMinWidth,
  LEADING_ROW_GUTTER,
} from "@src/lib/ui/tableColumnSizing";
import { TableHeaderRow } from "@src/lib/ui/Table";
import { TableSkeleton } from "@src/lib/ui/TableSkeleton";
import Text, { TextOverrideProps } from "@src/lib/ui/Text";
import { Input } from "@src/lib/ui/Input";
import { useScreenDimensions } from "@src/lib/hooks/useScreenDimensions";
import { downloadCSV } from "@src/lib/utils/csv";
import { addMissingVisibleColumnsToExportConfigUsingReactTextExtraction } from "@src/lib/utils/tableExportUtils";
import { debounce } from "lodash";
import { createPortal } from "react-dom";
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import { useTableWithSidebar } from "@src/features/sidebars/hooks/useTableWithDetailSidebar";
import { ViewportList } from "react-viewport-list";
import {
  ExportConfig,
  HeaderCellStyles,
  HeaderTooltipCopy,
  KeyType,
  MultiSelectConfig,
  RenderFns,
  ResponsivenessConfigs,
  SortConfig,
  TableItemCount,
} from "@src/lib/ui/Table";
import {
  LazyQueryResultTuple,
  QueryHookOptions,
  QueryResult,
} from "@apollo/client";
import { InputMaybe, PaginationInput } from "@src/types.generated";
import { JsonParam, useQueryParam } from "use-query-params";
import Spinner from "@src/lib/ui/Spinner";
import { Route } from "nextjs-routes";
import {
  isNotNullOrUndefined,
  isNullOrUndefined,
} from "@src/lib/utils/typeguards";
import { useDebouncedQueryParam } from "@src/lib/hooks/useDebouncedQueryParam";
import { useTracking } from "@src/features/app/hooks/useTracking";
import { useRouter } from "next/router";
import { useMediaQuery } from "@src/lib/hooks/useMediaQuery";
import { Button } from "@src/lib/ui/Button";
import ArrowDownloadIcon from "@src/lib/icons/18px/arrow-download";
import { ColumnSelector } from "@src/lib/ui/ColumnSelector";
import { useColumnSelection } from "@src/features/app/hooks/useColumnSelection";
import SearchIcon from "@src/lib/icons/18px/search";
import {
  getDefaultTableColumnWidth,
  getTableColumnOuterWidth,
  useColumnWidths,
} from "@src/lib/hooks/useColumnWidths";

const DEFAULT_LIMIT = 40;

/** What {@link TableProps.onCountChange} hands back — enough to render a
 * {@link TableItemCount} outside the table. */
export type TableCountReport = {
  /** Rows matching the current search/filters. */
  count: number | undefined;
  /** Rows before search/filters, from `allItemsCountInput`. */
  totalCount: number | undefined;
  loading: boolean;
};

type NoSearchResultsContext = { search: string; clearSearch: () => void };

/** An empty search, in words, with the way out. Opt in through
 * {@link TableProps.noSearchResultsEmptyState}. */
export const NoSearchResultsEmptyState: React.FC<NoSearchResultsContext> = ({
  search,
  clearSearch,
}) => (
  <div className="flex flex-col items-center gap-1 text-center">
    <Text variant="compact-title" className="text-text-primary">
      No results for “{search}”
    </Text>
    <Text variant="helper-description" className="text-text-tertiary">
      Try a different search term.
    </Text>
    <Button variant="outline" size="sm" className="mt-3" onClick={clearSearch}>
      Clear search
    </Button>
  </div>
);

export type RowHeight =
  | number
  | { wrap: true; minHeight: number; maxHeight: number };

// Variable shape expected by paginated query hooks
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PaginatedVariables = {
  input: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  pagination?: InputMaybe<PaginationInput> | undefined;
};

// Apollo Client 3.8+ generates hooks that require `variables` to be explicitly
// provided (or `skip: true`), so we mirror that intersection here.
type QueryHook = (
  baseOptions: QueryHookOptions<
    any, // eslint-disable-line @typescript-eslint/no-explicit-any
    PaginatedVariables
  > &
    ({ variables: PaginatedVariables; skip?: boolean } | { skip: boolean }),
) => QueryResult<any, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
type LazyQueryHook = (
  baseOptions: QueryHookOptions<
    any, // eslint-disable-line @typescript-eslint/no-explicit-any
    PaginatedVariables
  >,
) => LazyQueryResultTuple<any, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export interface TableProps<
  T,
  Column extends string,
  Hook extends QueryHook,
  LazyHook extends LazyQueryHook,
  ExportColumn extends string,
> {
  queryHook: Hook;
  lazyQueryHook: LazyHook;
  getItems: (data: ReturnType<Hook>["data"]) => T[];
  getTotalItemCount: (data: ReturnType<Hook>["data"]) => number;
  itemId: (item: T) => string | number;
  title?: string | React.ReactNode;
  allColumns: Column[];
  visibleColumns?: () => Column[];
  columnTitles?: Record<Column, string>;
  renderFns: RenderFns<T, Column>;
  renderFnsTextProps?: Partial<Record<Column, TextOverrideProps>>;
  responsiveness?: ResponsivenessConfigs<Column>;
  multiselect?: MultiSelectConfig<T>;
  rowStyles?: CSSProperties;
  rowHeight?: RowHeight;
  onRowClick?: (item: T, e: React.MouseEvent<HTMLDivElement>) => void;
  onRowHover?: (item: T) => void;
  rowClickLink?: (item: T) => Route | undefined | null;
  disableRowClickOnColumns?: Set<Column>;
  overflowMenu?: (item: T) => React.ReactNode;
  rightAction?: React.ReactNode;
  leftAction?: React.ReactNode;
  /**
   * Renders inline in the toolbar row just before the Export button (after
   * search + {@link rightAction}). Use for compact controls that should sit
   * next to export, e.g. a “show details” toggle on reporting tables.
   */
  preExportAction?: React.ReactNode;
  /** Sits after Export — for a way out of the table, like a link to a related report. */
  postExportAction?: React.ReactNode;
  loading?: boolean;
  headerStickyPositioning?: number;
  noDataEmptyState?: React.ReactNode;
  /**
   * Shown instead of `noDataEmptyState` when the table is empty because of
   * the search box. Without it the search-empty table reads as any other
   * empty table.
   */
  noSearchResultsEmptyState?:
    | React.ReactNode
    | ((ctx: NoSearchResultsContext) => React.ReactNode);
  fullEmptyState?: React.ReactNode;
  exportConfig?: ExportConfig<T, ExportColumn>;
  defaultSort?: SortConfig<Column>;
  disabledSorts?: Column[];
  filters?: NonNullable<Parameters<QueryHook>[0]["variables"]>["input"];
  input?: NonNullable<Parameters<QueryHook>[0]["variables"]>["input"];
  allItemsCountInput?: NonNullable<
    Parameters<QueryHook>[0]["variables"]
  >["input"];
  hideCount?: boolean;
  /**
   * Reports the table's own count whenever it settles, so a host can render it
   * somewhere the table can't reach — e.g. beside a section heading above it.
   * Pair with {@link hideCount} to avoid showing the number twice. Pass a
   * stable callback: it is called from an effect keyed on the count.
   */
  onCountChange?: (report: TableCountReport) => void;
  pollInterval?: number;
  activeColumn?: Column;
  headerCellStyles?: HeaderCellStyles<Column>;
  headerHeight?: RowHeight;
  enableSearch?: boolean;
  /**
   * URL query param backing the search box. Defaults to "q". Override for
   * tables nested inside a page whose own search already uses "q" (e.g. the
   * library list), so the page-level search term doesn't leak into the table.
   */
  searchQueryParamName?: string;
  showSortDirection?: boolean;
  headerTooltipCopy?: HeaderTooltipCopy<Column>;
  multilineHeader?: boolean;
  tableStyles?: CSSProperties;
  headerBreakPoint?: number;
  selectedLibraryItemIdsFromBulkUpload?: string[];
  SubHeader?: React.ReactNode;
  onColumnHeaderClick?: (column: Column) => void;
  allItemsSelected?: boolean;
  dataTestId?: string;
  maxItems?: number;
  sidebarSelectedRowId?: string | number;
  columnSelection?:
    | {
        enabled: boolean;
        alwaysVisibleColumns?: Column[];
        defaultSelectedColumns?: Column[];
        placement?: "left" | "right";
      }
    | boolean; // shorthand for { enabled: true }
  sidebarConfig?: {
    tableId: string;
    sidebarType: DetailSidebarType;
    enabled: boolean;
  };
  pageSize?: number;
  columnCollapseScreenWidths?: Partial<Record<Column, number>>;
  /**
   * Optional DOM node to portal the filter-action row (left actions + search +
   * column selector + export) into, instead of rendering it inline above the
   * table. Useful for lifting the filter bar into a page-level subheader.
   */
  filterBarContainer?: HTMLElement | null;
  /**
   * Where a portaled filter bar puts search and the column selector. The
   * top-nav row wants them left-aligned with the page shell ("left", the
   * default). Pass "right" when the host already owns the left of the row —
   * e.g. Reporting → People's facet chips — so the control cluster stays together.
   */
  filterBarAlign?: "left" | "right";
  horizontalScroll?: boolean;
  viewportRef?: React.RefObject<HTMLElement | null>;
  hideFooterWhenComplete?: boolean;
  stickyFirstColumn?: boolean;
  resizableColumns?: boolean;
  columnWidthPersistenceKey?: string;
  trailingEmptyColumnWidth?: number;
  /**
   * `horizontalScroll` shorthand: only this column grows into leftover width.
   * See {@link Table}.
   */
  growableColumn?: Column;
  /**
   * When true, the data portion of the table (header row + data rows + its
   * horizontal-scroll container) is pulled back to the edges of the nearest
   * `px-6` ancestor with negative horizontal margin. The toolbar / filter bar
   * / {@link SubHeader} remain within the padded column so only the rows run
   * edge-to-edge. Use this for reporting tables nested inside a padded
   * content shell.
   */
  fullBleedTable?: boolean;
}

export default function PaginatedTable<
  T,
  Column extends string,
  Hook extends QueryHook,
  LazyHook extends LazyQueryHook,
  ExportColumn extends string,
>({
  allColumns,
  lazyQueryHook,
  itemId,
  visibleColumns,
  columnTitles,
  title,
  renderFns,
  renderFnsTextProps,
  responsiveness,
  multiselect,
  rowStyles,
  rowHeight = 42,
  onRowClick,
  onRowHover,
  rowClickLink,
  overflowMenu,
  leftAction,
  rightAction,
  preExportAction,
  postExportAction,
  onColumnHeaderClick,
  headerStickyPositioning,
  disableRowClickOnColumns,
  noDataEmptyState,
  noSearchResultsEmptyState,
  fullEmptyState,
  exportConfig,
  queryHook,
  input,
  allItemsCountInput,
  hideCount,
  onCountChange,
  getItems,
  getTotalItemCount,
  defaultSort,
  pollInterval,
  disabledSorts,
  activeColumn,
  headerCellStyles,
  enableSearch = true,
  searchQueryParamName = "q",
  showSortDirection,
  headerHeight,
  headerTooltipCopy,
  multilineHeader = false,
  tableStyles,
  headerBreakPoint,
  selectedLibraryItemIdsFromBulkUpload,
  SubHeader,
  allItemsSelected = false,
  dataTestId,
  columnSelection,
  maxItems,
  sidebarSelectedRowId,
  sidebarConfig,
  pageSize = DEFAULT_LIMIT,
  columnCollapseScreenWidths,
  filterBarContainer,
  filterBarAlign,
  horizontalScroll = false,
  viewportRef,
  hideFooterWhenComplete = false,
  stickyFirstColumn = false,
  resizableColumns = false,
  columnWidthPersistenceKey,
  trailingEmptyColumnWidth,
  growableColumn,
  fullBleedTable = false,
}: TableProps<T, Column, Hook, LazyHook, ExportColumn>): React.ReactElement {
  const {
    value: search,
    setValue: setSearch,
    debouncedValue: debouncedSearch,
  } = useDebouncedQueryParam({
    queryParamName: searchQueryParamName,
    initialValue: "",
    delay: 300,
  });
  // `sortParam` is only present when the user explicitly picked a sort (e.g.
  // clicked a column header); `defaultSort` fills in otherwise. The
  // distinction matters while searching: the backend orders search results by
  // relevance, and only an explicit sort overrides that.
  const [sortParam, setSort] = useQueryParam<SortConfig<Column> | undefined>(
    "sort",
    JsonParam,
  );
  const explicitSort = sortParam != null;
  const sort = sortParam ?? defaultSort;
  // Tables without a search box must never treat the URL `search` param as an
  // active search — the user can't see or clear it, and letting it through
  // both leaks the value to the backend (some reject it) and silently
  // suppresses the default sort. Gate on `enableSearch` at the source.
  const searchActive = enableSearch && debouncedSearch.length > 0;
  const ref = useRef<HTMLDivElement | null>(null);

  // Normalize columnSelection config
  const normalizedColumnSelection = useMemo(() => {
    if (typeof columnSelection === "boolean") {
      return columnSelection ? { enabled: true } : { enabled: false };
    }
    return columnSelection;
  }, [columnSelection]);

  const { width: screenWidth } = useScreenDimensions();

  const defaultSelectedColumns = useMemo(
    () =>
      (normalizedColumnSelection?.defaultSelectedColumns || allColumns).filter(
        (column) => {
          const collapseWidth = columnCollapseScreenWidths?.[column];
          return collapseWidth === undefined || screenWidth > collapseWidth;
        },
      ),
    [
      normalizedColumnSelection,
      allColumns,
      columnCollapseScreenWidths,
      screenWidth,
    ],
  );

  // Column selection state management
  const {
    selectedColumns: userSelectedColumns,
    setSelectedColumns: setUserSelectedColumns,
    visibleColumnsFunction: dynamicVisibleColumns,
  } = useColumnSelection({
    allColumns,
    defaultSelectedColumns,
    key: sidebarConfig?.tableId, // Use tableId as unique key to prevent interference
  });

  // Use either the provided visibleColumns function or the dynamic one
  const finalVisibleColumns = useMemo(() => {
    if (normalizedColumnSelection?.enabled) {
      return dynamicVisibleColumns;
    }
    return visibleColumns;
  }, [
    normalizedColumnSelection?.enabled,
    dynamicVisibleColumns,
    visibleColumns,
  ]);

  const _input = useMemo(() => {
    return {
      ...input,
      // `searchActive` already accounts for `enableSearch`, so a leaked URL
      // search param on a no-search-box table never reaches the backend.
      search: searchActive ? { value: debouncedSearch } : undefined,
      // While searching, the backend orders results by relevance and only an
      // explicit (user-selected) sort overrides it — so default sorts are
      // omitted and explicit ones are flagged.
      sort:
        sort && (!searchActive || explicitSort)
          ? {
              ...sort,
              nullsLast: sort.nullsLast ?? defaultSort?.nullsLast,
              explicit: explicitSort || undefined,
            }
          : undefined,
    };
  }, [
    debouncedSearch,
    searchActive,
    explicitSort,
    input,
    sort,
    defaultSort?.nullsLast,
  ]);
  const { data, loading, fetchMore, error } = queryHook({
    pollInterval,
    variables: {
      input: _input,
      pagination: {
        limit: maxItems ? maxItems : pageSize,
        offset: 0,
      },
    },
  });
  // `hideCount` only hides the inline number — a host reading the count through
  // `onCountChange` still needs the query to run.
  const skipTotalCount =
    (hideCount && !onCountChange) || isNullOrUndefined(allItemsCountInput);
  const { data: totalCountData, loading: totalCountLoading } = queryHook({
    skip: skipTotalCount,
    variables: {
      input: allItemsCountInput || {},
      pagination: {
        limit: 0,
      },
    },
    // Paginated fields are cached with `keyArgs: ["input"]` so that pages merge
    // across `pagination`, which means this limit-0 count shares a cache entry
    // with the list query whenever the two inputs match (i.e. whenever no
    // filter is applied). Cached, whichever response lands first wins: if it is
    // this one, the list query reads back its empty `objects` as a complete
    // result and renders an empty table without ever hitting the network.
    // `no-cache` keeps the count out of that entry entirely.
    fetchPolicy: "no-cache",
  });
  const [fetchAll, { loading: fetchAllLoading }] = lazyQueryHook({
    variables: {
      input: _input,
    },
  });
  const isDesktop = useMediaQuery(`(min-width: ${headerBreakPoint || 1}px)`);
  const items = useMemo(() => {
    const allItems = getItems(data);
    // Explicitly slice results to respect maxItems limit, even if cached data has more
    return maxItems ? allItems.slice(0, maxItems) : allItems;
  }, [data, getItems, maxItems]);
  const itemCount = useMemo(() => {
    return getTotalItemCount(data);
  }, [data, getTotalItemCount]);
  // Without the count query there is no "of N" to compare against, so the
  // unfiltered total falls back to the filtered one and reads as a plain number.
  const allItemsCount = useMemo(
    () => (skipTotalCount ? undefined : getTotalItemCount(totalCountData)),
    [skipTotalCount, totalCountData, getTotalItemCount],
  );
  // An effect rather than a render-time call: the host holds this in state.
  useEffect(() => {
    onCountChange?.({
      count: itemCount,
      totalCount: allItemsCount ?? itemCount,
      loading: totalCountLoading,
    });
  }, [onCountChange, itemCount, allItemsCount, totalCountLoading]);

  const { trackEvent } = useTracking();
  const router = useRouter();
  // Report searches that settle on zero results — a content-findability
  // signal. The delay skips intermediate keystrokes: any change to the
  // search (or fresh loading) cancels the pending event. `hasData` (not
  // `data`) keeps polling tables from re-running the effect, and the ref
  // ensures at most one event per settled search term.
  const hasData = !isNullOrUndefined(data);
  const searchIsSettled = search === debouncedSearch;
  const lastTrackedZeroResultSearch = useRef<string | null>(null);
  useEffect(() => {
    if (
      loading ||
      !hasData ||
      !enableSearch ||
      !searchIsSettled ||
      debouncedSearch.length === 0 ||
      itemCount !== 0 ||
      lastTrackedZeroResultSearch.current === debouncedSearch
    ) {
      return;
    }
    const timeout = setTimeout(() => {
      lastTrackedZeroResultSearch.current = debouncedSearch;
      trackEvent("table_search_zero_results", {
        searchText: debouncedSearch,
        pathname: router.pathname,
      });
    }, 1500);
    return () => clearTimeout(timeout);
  }, [
    loading,
    hasData,
    enableSearch,
    searchIsSettled,
    debouncedSearch,
    itemCount,
    trackEvent,
    router.pathname,
  ]);

  // Internal sidebar management (when sidebarConfig is provided)
  // Always call the hook but conditionally use its results
  const internalSidebarState = useTableWithSidebar({
    tableId: sidebarConfig?.tableId || "disabled",
    items: sidebarConfig?.enabled ? items : [], // Use empty array when disabled
    itemId: (item: T) => String(itemId(item)),
    sidebarType: sidebarConfig?.sidebarType || DetailSidebarType.User,
  });

  // Use internal sidebar state if configured, otherwise use external props
  const finalOnRowClick = sidebarConfig?.enabled
    ? internalSidebarState.onRowClick
    : onRowClick;
  const finalSidebarSelectedRowId = sidebarConfig?.enabled
    ? internalSidebarState.sidebarSelectedRowId
    : sidebarSelectedRowId;

  const [lastSelectedItemId, setLastSelectedItemId] = useState<KeyType>();
  const [allFilteredItemsSelected, setAllFilteredItemsSelected] =
    useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<KeyType>>(
    new Set(),
  );
  const maybeFetchAndSelectAllItems = useCallback(() => {
    if (fetchAll) {
      fetchAll()
        .then(({ data }) => {
          if (data) {
            const items = getItems(data);
            setSelectedItemIds(new Set(items.map(itemId)));
          }
        })
        .then(() => {
          setAllFilteredItemsSelected(true);
        });
    }
  }, [fetchAll, getItems, itemId]);

  useEffect(() => {
    if (selectedLibraryItemIdsFromBulkUpload) {
      setSelectedItemIds(new Set(selectedLibraryItemIdsFromBulkUpload));
    }
    if (allItemsSelected) {
      maybeFetchAndSelectAllItems();
    }
  }, [
    maybeFetchAndSelectAllItems,
    selectedLibraryItemIdsFromBulkUpload,
    allItemsSelected,
  ]);

  const debouncedFetchMore = debounce(() => {
    if (maxItems && items.length >= maxItems) {
      return;
    }
    fetchMore({
      variables: {
        input: _input,
        pagination: {
          offset: items.length,
          limit: pageSize,
        },
      },
    });
  }, 500);
  const toggleItemSelected = useCallback(
    (item: T, holdingShift: boolean) => {
      setLastSelectedItemId(itemId(item));
      setSelectedItemIds((oldState) => {
        const newState = new Set(oldState);
        if (newState.has(itemId(item))) {
          newState.delete(itemId(item));
          setAllFilteredItemsSelected(false);
        } else {
          newState.add(itemId(item));
        }
        return newState;
      });
      if (!holdingShift) {
        return;
      }

      const lastSelectedItemIndex = items.findIndex((x) => {
        return itemId(x) === lastSelectedItemId;
      });
      const toggledItemIndex = items.findIndex((x) => {
        return itemId(x) === itemId(item);
      });
      if (lastSelectedItemIndex === -1 || toggledItemIndex === -1) {
        return;
      }
      const min = Math.min(lastSelectedItemIndex, toggledItemIndex);
      const max = Math.max(lastSelectedItemIndex, toggledItemIndex);
      const newSelectedItemIds = new Set(selectedItemIds);
      items.slice(min, max + 1).forEach((x) => {
        if (selectedItemIds.has(itemId(item))) {
          newSelectedItemIds.delete(itemId(x));
        } else {
          newSelectedItemIds.add(itemId(x));
        }
      });
      setSelectedItemIds(newSelectedItemIds);
    },
    [itemId, items, lastSelectedItemId, selectedItemIds],
  );
  const toggleAllFilteredItemsSelected = useCallback(() => {
    setAllFilteredItemsSelected(!allFilteredItemsSelected);
    if (allFilteredItemsSelected) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(
        new Set(
          items
            .filter((x) => {
              return !(
                multiselect?.disableMultiSelectForItem &&
                multiselect.disableMultiSelectForItem(x)
              );
            })
            .map((x) => itemId(x)),
        ),
      );
    }
  }, [allFilteredItemsSelected, itemId, items, multiselect]);
  const deselectAllSelectedItems = useCallback(() => {
    setAllFilteredItemsSelected(false);
    setSelectedItemIds(new Set());
  }, [setSelectedItemIds]);
  const columnsToDisplay: Column[] = useMemo(
    () => finalVisibleColumns?.() || allColumns,
    [finalVisibleColumns, allColumns],
  );
  const defaultColumnWidths = useMemo(() => {
    const w = {} as Record<Column, number>;
    for (const c of allColumns) {
      w[c] = getDefaultTableColumnWidth(
        c === columnsToDisplay[0],
        responsiveness?.[c]?.width,
        responsiveness?.[c]?.minWidth,
      );
    }
    return w;
  }, [allColumns, columnsToDisplay, responsiveness]);

  const columnMinWidths = useMemo(() => {
    const m = {} as Partial<Record<Column, number>>;
    for (const c of allColumns) {
      if (responsiveness?.[c]?.minWidth != null) {
        m[c] = responsiveness[c].minWidth!;
      }
    }
    return m;
  }, [allColumns, responsiveness]);

  const {
    widths: columnWidths,
    resizedColumns,
    commitWidth: commitColumnWidth,
  } = useColumnWidths(
    horizontalScroll ? columnWidthPersistenceKey : undefined,
    allColumns,
    defaultColumnWidths,
    columnMinWidths,
    // A sticky first column never grew before :v4, so its saved width was a
    // real width, not a growth floor; keep it.
    growableColumn === columnsToDisplay[0] && stickyFirstColumn
      ? undefined
      : growableColumn,
  );

  const columnSizing = useMemo(
    () =>
      horizontalScroll
        ? computeTableColumnSizing({
            columns: columnsToDisplay,
            widths: columnWidths,
            responsiveness,
            growableColumn,
            resizedColumns,
          })
        : undefined,
    [
      horizontalScroll,
      columnsToDisplay,
      columnWidths,
      responsiveness,
      growableColumn,
      resizedColumns,
    ],
  );

  // The trailing gutter is a fixed spacer on every row, so it counts toward
  // the min width; leftover space goes to the data columns (`columnSizing`).
  const totalTableMinWidth = useMemo(
    () =>
      horizontalScroll
        ? getHorizontalScrollTableMinWidth({
            columnBases: columnsToDisplay.map((col) =>
              getTableColumnOuterWidth(columnWidths[col]),
            ),
            checkboxColumnWidth: multiselect?.checkboxColumnWidth,
            hasOverflowMenu: !!overflowMenu,
            trailingEmptyColumnWidth,
          })
        : undefined,
    [
      horizontalScroll,
      multiselect,
      columnsToDisplay,
      columnWidths,
      overflowMenu,
      trailingEmptyColumnWidth,
    ],
  );

  const checkboxOuterWidth = useMemo(
    () =>
      multiselect != null
        ? getTableColumnOuterWidth(multiselect.checkboxColumnWidth)
        : LEADING_ROW_GUTTER,
    [multiselect],
  );

  const tableHScrollRef = useRef<HTMLDivElement>(null);
  const [stickyScrollEdgeShadow, setStickyScrollEdgeShadow] = useState(false);
  const syncStickyScrollShadow = useCallback(() => {
    const el = tableHScrollRef.current;
    if (!el) return;
    setStickyScrollEdgeShadow(el.scrollLeft > 0);
  }, []);

  useEffect(() => {
    if (!horizontalScroll || !stickyFirstColumn) {
      setStickyScrollEdgeShadow(false);
      return;
    }
    const el = tableHScrollRef.current;
    if (!el) return;
    syncStickyScrollShadow();
    el.addEventListener("scroll", syncStickyScrollShadow, { passive: true });
    window.addEventListener("resize", syncStickyScrollShadow);
    return () => {
      el.removeEventListener("scroll", syncStickyScrollShadow);
      window.removeEventListener("resize", syncStickyScrollShadow);
    };
  }, [
    horizontalScroll,
    stickyFirstColumn,
    syncStickyScrollShadow,
    totalTableMinWidth,
    columnsToDisplay,
  ]);

  const showLoadingState = useMemo(() => loading && !data, [loading, data]);
  const showNoSearchResultsEmptyState = useMemo(() => {
    return (
      itemCount !== undefined &&
      itemCount > 0 &&
      items.length === 0 &&
      !showLoadingState
    );
  }, [items.length, showLoadingState, itemCount]);
  const selectedItems = useMemo(
    () => items.filter((x) => selectedItemIds.has(itemId(x))),
    [items, itemId, selectedItemIds],
  );

  // Portaling normally hands search and the column selector to the left group
  // so they line up with the page shell's top-nav row. `filterBarAlign: "right"`
  // opts out for hosts that already fill the left of the row themselves.
  const searchInLeftGroup =
    Boolean(filterBarContainer) && filterBarAlign !== "right";
  const columnSelectorPlacement = searchInLeftGroup
    ? "left"
    : normalizedColumnSelection?.placement ?? "right";

  const columnSelectorAction = useMemo(() => {
    if (!normalizedColumnSelection?.enabled) {
      return undefined;
    }

    return (
      <ColumnSelector
        key="column-selector"
        allColumns={allColumns}
        selectedColumns={userSelectedColumns}
        onSelectionChange={setUserSelectedColumns}
        columnTitles={columnTitles}
        alwaysVisibleColumns={normalizedColumnSelection.alwaysVisibleColumns}
        defaultSelectedColumns={
          normalizedColumnSelection.defaultSelectedColumns || allColumns
        }
      />
    );
  }, [
    normalizedColumnSelection,
    allColumns,
    userSelectedColumns,
    setUserSelectedColumns,
    columnTitles,
  ]);

  const tableLeftAction = useMemo(() => {
    const actions = [];

    if (leftAction) {
      actions.push(leftAction);
    }

    if (columnSelectorPlacement === "left" && columnSelectorAction) {
      actions.push(columnSelectorAction);
    }

    if (actions.length === 0) return undefined;
    if (actions.length === 1) return actions[0];

    // `contents`: each control joins the filter row's own wrapping instead of
    // a box that squeezes its children into a column.
    return <div className="contents">{actions}</div>;
  }, [leftAction, columnSelectorPlacement, columnSelectorAction]);

  // Create combined right action with column selector
  const tableRightAction = useMemo(() => {
    const actions = [];

    if (columnSelectorPlacement !== "left" && columnSelectorAction) {
      actions.push(columnSelectorAction);
    }

    // Add any existing right action
    if (rightAction) {
      actions.push(rightAction);
    }

    if (actions.length === 0) return undefined;
    if (actions.length === 1) return actions[0];

    return <div className="flex gap-2">{actions}</div>;
  }, [columnSelectorPlacement, columnSelectorAction, rightAction]);
  const [exportLoading, setExportLoading] = useState(false);
  const hasMore = useMemo(() => {
    return itemCount !== undefined && items.length < itemCount;
  }, [itemCount, items.length]);
  const hasNoData = useMemo(
    () =>
      !showLoadingState && itemCount !== undefined && itemCount === 0 && !error,
    [showLoadingState, itemCount, error],
  );
  const hasError = useMemo(() => {
    return !!error;
  }, [error]);
  const downloadCSVWrapper = useCallback(async () => {
    if (!exportConfig) {
      return;
    }
    setExportLoading(true);
    const { data } = await fetchAll();
    const rows = getItems(data);
    setExportLoading(false);
    if (!rows) {
      return;
    }

    const currentVisibleColumns = finalVisibleColumns
      ? finalVisibleColumns()
      : allColumns;

    const enhancedConfig =
      addMissingVisibleColumnsToExportConfigUsingReactTextExtraction(
        exportConfig,
        currentVisibleColumns,
        renderFns,
        columnTitles,
      );

    downloadCSV(
      enhancedConfig.title,
      enhancedConfig.combinedColumnTitles,
      rows.map((i) => {
        return enhancedConfig.allExportColumns.map((c) =>
          enhancedConfig.combinedExportFns[c](i),
        );
      }),
    );
  }, [
    exportConfig,
    fetchAll,
    getItems,
    finalVisibleColumns,
    allColumns,
    renderFns,
    columnTitles,
  ]);

  const ExportButton = useMemo(() => {
    if (!exportConfig) {
      return null;
    }
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled={exportLoading}
        onClick={downloadCSVWrapper}
        className={TABLE_TOOLBAR_BUTTON_CLASSNAME}
      >
        <ArrowDownloadIcon className={TABLE_TOOLBAR_LEADING_ICON_CLASSNAME} />
        Export
      </Button>
    );
  }, [exportConfig, downloadCSVWrapper, exportLoading]);

  if (hasNoData && fullEmptyState) {
    return <div className="flex flex-1 self-stretch">{fullEmptyState}</div>;
  }

  return (
    <div
      id={"paginated-table-container"}
      className="flex min-w-0 flex-col self-stretch"
      data-testid={dataTestId}
    >
      {(tableRightAction ||
        exportConfig ||
        tableLeftAction ||
        title ||
        enableSearch ||
        preExportAction ||
        postExportAction ||
        (!hideCount && isNotNullOrUndefined(allItemsCountInput))) &&
        (() => {
          const searchControl = enableSearch ? (
            <div className="flex items-center gap-2">
              <div className="flex w-[200px] max-w-[200px]">
                <div className="relative w-full">
                  <SearchIcon className={TABLE_TOOLBAR_SEARCH_ICON_CLASSNAME} />
                  <Input
                    placeholder="Search..."
                    aria-label="Search"
                    value={search || ""}
                    onChange={(e) => setSearch(e.target.value)}
                    inputClassName={TABLE_TOOLBAR_SEARCH_INPUT_CLASSNAME}
                  />
                </div>
              </div>
              {searchActive && !explicitSort && (
                <Text
                  type="P3"
                  className="whitespace-nowrap text-text-secondary"
                >
                  Sorted by best match
                </Text>
              )}
            </div>
          ) : null;
          // Next to a title the count reads as part of it ("Reviews  1 of 5"), so it
          // stays inline as a bare number. With no title it would float alone at the
          // left of the filter row, so it gets its own narrow row above the filters
          // and spells itself out ("Showing 10 of 20 items"). A portaled filter bar
          // keeps the inline placement — the host container owns that row.
          const countInline = Boolean(title) || Boolean(filterBarContainer);
          const countBadge =
            !hideCount && isNotNullOrUndefined(allItemsCountInput) ? (
              <TableItemCount
                loading={totalCountLoading}
                count={itemCount}
                totalCount={getTotalItemCount(totalCountData)}
                labelled={!countInline}
              />
            ) : null;
          const filterBar = (
            <div
              className={cn(
                "flex self-stretch items-center py-0.5",
                isDesktop
                  ? "flex-row flex-wrap gap-2"
                  : "flex-col justify-between gap-2",
                filterBarContainer && "order-last min-w-0 w-full flex-1",
              )}
            >
              {/* On desktop every control is an item of the one wrapping
                  row: narrow, the filters wrap chip by chip instead of
                  squeezing into a column, and the right-hand group stays
                  together at the row's end. */}
              <div
                className={
                  isDesktop ? "contents" : "flex flex-auto items-center gap-2"
                }
              >
                {searchInLeftGroup && searchControl}
                {tableLeftAction}
                {title && (
                  <div className="mr-2 flex">
                    {typeof title === "string" ? (
                      <Text variant="body-title">{title}</Text>
                    ) : (
                      title
                    )}
                  </div>
                )}
                {showLoadingState ? <div /> : countInline ? countBadge : null}
              </div>
              <div
                className={cn(
                  "flex min-w-0 flex-wrap items-center justify-end gap-2",
                  isDesktop && "ml-auto",
                )}
              >
                {!searchInLeftGroup && searchControl}
                {tableRightAction}
                {preExportAction}
                {ExportButton}
                {postExportAction}
              </div>
            </div>
          );
          if (filterBarContainer) {
            return createPortal(filterBar, filterBarContainer);
          }
          return (
            <>
              {!countInline && !showLoadingState && countBadge && (
                <div className="flex self-stretch py-0.5">{countBadge}</div>
              )}
              {filterBar}
            </>
          );
        })()}
      {SubHeader}
      <div
        ref={horizontalScroll ? tableHScrollRef : undefined}
        className={cn(
          "self-stretch",
          (horizontalScroll || tableStyles?.overflowX) &&
            "overflow-x-auto overflow-y-visible",
          fullBleedTable && "-mx-6",
        )}
      >
        <div
          data-table-inner={horizontalScroll ? "true" : undefined}
          className={cn(
            "flex flex-col",
            horizontalScroll ? "min-w-0 self-start" : "self-stretch",
            !horizontalScroll && tableStyles?.overflowX && "min-w-fit",
          )}
          style={
            horizontalScroll && totalTableMinWidth != null
              ? { minWidth: totalTableMinWidth, width: "100%" }
              : tableStyles
          }
        >
          {columnTitles && (
            <TableHeaderRow
              columns={columnsToDisplay}
              columnTitles={columnTitles}
              responsiveness={responsiveness}
              multiselect={multiselect}
              selectedSort={searchActive && !explicitSort ? undefined : sort}
              setSelectedSort={(val) => {
                setSort?.(val);
              }}
              onColumnHeaderClick={onColumnHeaderClick}
              allFilteredItemsSelected={allFilteredItemsSelected}
              toggleAllFilteredItemsSelected={toggleAllFilteredItemsSelected}
              includeOverflowMenuColumn={!!overflowMenu}
              stickyPositioning={headerStickyPositioning}
              isSkeletonState={showLoadingState}
              disabledSorts={disabledSorts}
              activeColumn={activeColumn}
              headerCellStyles={headerCellStyles}
              headerHeight={headerHeight}
              showSortDirection={showSortDirection}
              headerTooltipCopy={headerTooltipCopy}
              multiline={multilineHeader}
              horizontalScroll={horizontalScroll}
              stickyFirstColumn={stickyFirstColumn}
              resizableColumns={resizableColumns}
              columnWidths={horizontalScroll ? columnWidths : undefined}
              onColumnResizeCommit={
                horizontalScroll ? commitColumnWidth : undefined
              }
              checkboxOuterWidth={checkboxOuterWidth}
              trailingEmptyColumnWidth={
                horizontalScroll ? trailingEmptyColumnWidth : undefined
              }
              columnSizing={columnSizing}
              stickyScrollEdgeShadow={stickyScrollEdgeShadow}
            />
          )}
          <div
            ref={ref}
            style={{
              alignSelf: "stretch",
              flexGrow: 1,
              flex: 1,
              height: itemCount === 0 ? 120 : "auto",
            }}
          >
            {!showLoadingState && (
              <ViewportList
                viewportRef={viewportRef}
                items={items}
                itemMinSize={
                  typeof rowHeight === "number" ? rowHeight : undefined
                }
                onViewportIndexesChange={([, end]) => {
                  if (hasMore && end >= items.length - 1) {
                    debouncedFetchMore();
                  }
                }}
              >
                {(item) => {
                  return (
                    <TableContentRow
                      key={itemId(item)}
                      item={item}
                      columns={columnsToDisplay}
                      renderFns={renderFns}
                      renderFnsTextProps={renderFnsTextProps}
                      multiselect={multiselect}
                      responsiveness={responsiveness}
                      isSelected={selectedItemIds.has(itemId(item))}
                      toggleItemSelected={toggleItemSelected}
                      rowStyles={rowStyles}
                      rowHeight={rowHeight}
                      onRowClick={finalOnRowClick}
                      onRowHover={onRowHover}
                      rowClickLink={rowClickLink}
                      disableRowClickOnColumns={disableRowClickOnColumns}
                      overflowMenu={overflowMenu}
                      activeColumn={activeColumn}
                      isSidebarSelected={
                        finalSidebarSelectedRowId !== undefined &&
                        String(itemId(item)) ===
                          String(finalSidebarSelectedRowId)
                      }
                      horizontalScroll={horizontalScroll}
                      stickyFirstColumn={stickyFirstColumn}
                      columnWidths={horizontalScroll ? columnWidths : undefined}
                      checkboxOuterWidth={checkboxOuterWidth}
                      trailingEmptyColumnWidth={
                        horizontalScroll ? trailingEmptyColumnWidth : undefined
                      }
                      columnSizing={columnSizing}
                      stickyScrollEdgeShadow={stickyScrollEdgeShadow}
                    />
                  );
                }}
              </ViewportList>
            )}
          </div>
          {showLoadingState && (
            <TableSkeleton
              columns={columnsToDisplay.map((col) => ({
                width: responsiveness?.[col]?.width,
                minWidth: responsiveness?.[col]?.minWidth,
                flex: responsiveness?.[col]?.flex,
                sizing: columnSizing?.[col],
              }))}
              horizontalScroll={horizontalScroll}
              trailingEmptyColumnWidth={trailingEmptyColumnWidth}
              rowHeight={
                typeof rowHeight === "number"
                  ? rowHeight
                  : rowHeight?.minHeight ?? 42
              }
              showOverflowColumn={!!overflowMenu}
              showCheckboxColumn={!!multiselect}
              checkboxColumnWidth={multiselect?.checkboxColumnWidth}
            />
          )}
        </div>
      </div>
      {/* Messages sit outside the horizontal scroll container: inside it they
          are laid out at the table's full column width, so on a narrow viewport
          the centered text lands off-screen to the right. */}
      {showNoSearchResultsEmptyState && (
        <div className="flex justify-center self-stretch pt-20">
          <Text variant="compact-title" className="text-text-tertiary">
            No results
          </Text>
        </div>
      )}
      {hasError && (
        <div className="flex justify-center self-stretch pt-20">
          <Text variant="compact-title" className="text-status-error-text">
            Error loading data
          </Text>
        </div>
      )}
      {hasNoData && (
        <div className="flex justify-center self-stretch pt-20">
          {searchActive && noSearchResultsEmptyState ? (
            typeof noSearchResultsEmptyState === "function" ? (
              noSearchResultsEmptyState({
                search: debouncedSearch,
                clearSearch: () => setSearch(""),
              })
            ) : (
              noSearchResultsEmptyState
            )
          ) : !noDataEmptyState || typeof noDataEmptyState === "string" ? (
            <Text variant="compact-title" className="text-text-tertiary">
              {noDataEmptyState || "Nothing to display"}
            </Text>
          ) : (
            noDataEmptyState
          )}
        </div>
      )}
      {multiselect && (
        <MultiSelectBanner
          numSelected={selectedItemIds.size}
          onDeselectAll={deselectAllSelectedItems}
          actions={multiselect.actions(selectedItems, deselectAllSelectedItems)}
          totalItemCount={itemCount}
          selectAllLoading={fetchAllLoading}
          selectAll={maybeFetchAndSelectAllItems}
          hasDestroyPermission={multiselect.hasDestroyPermission}
          destroyLabel={multiselect.destroyLabel}
          onDestroy={
            multiselect.onDestroy
              ? () => {
                  if (multiselect.onDestroy) {
                    multiselect.onDestroy(
                      selectedItems,
                      deselectAllSelectedItems,
                    );
                  }
                }
              : undefined
          }
        />
      )}
      {(!hideFooterWhenComplete || (hasMore && !maxItems)) && (
        <div
          className="flex items-center justify-center"
          style={{
            height: 112,
            width: "100%",
          }}
        >
          {hasMore && !maxItems && <Spinner />}
        </div>
      )}
    </div>
  );
}
